import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Row,
  Select,
  Skeleton,
  Table,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "../../Context/ThemeContext";
import { useClient } from "../../Context/ClientContext";
import { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  LedgerStatus,
  LedgerStatusDropDown,
} from "../../Services/LedgerService";
import { notificationContext } from "../../Common/Routing/PageRoute";
import OrganisationsService, {
  IDesignationData,
} from "../../Services/Organisations/OrganisationsService";
import { OrgDataType } from "./Organisations";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import AddDesignationModel from "./AddDesignationModel";
import { tablePageSizeOptions } from "../../Common/ThemeSetting";

interface DesignationDataType extends OrgDataType {
  designationName: string;
  subUnitName: string;
  depttName: string;
}

const Designations = () => {
  const { currentClient } = useClient();
  const [openModel, setOpenModel] = useState<{ type: string; param: any }>({
    type: "",
    param: {},
  });
  const [addUpdateTrigger, setAddUpdateTrigger] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const { themeData } = useTheme();
  const { openNotification } = useContext(notificationContext);
  const [designationsList, setDesignationsList] = useState<
    DesignationDataType[]
  >([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    themeData?.tablePageSize ?? 15
  );
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filter, setfilter] = useState<any>({
    sortCol: "",
    sortDir: "",
  });
  const [trigger, setTrigger] = useState<boolean>(false);
  const [ledgerStatus, setLedgerStatus] = useState<LedgerStatus>(
    LedgerStatus.active
  );

  const getDesignationsList = async () => {
    setDataLoading(true);
    await OrganisationsService.getDesignationsList(
      currentClient?.id!,
      (page - 1) * pageSize,
      pageSize,
      searchText,
      filter.sortCol,
      filter.sortDir,
      ledgerStatus
    ).then((val) => {
      if (val?.items) {
        setDesignationsList(
          val.items?.map<DesignationDataType>(
            (des: IDesignationData, index: number) => {
              return {
                sno: des.sno,
                id: des.id as string,
                designationName: des.designationName as string,
                orgName: des.orgName as string,
                subUnitName: des.subUnitName as string,
                depttName: des.depttName as string,
                status: des.status!,
              };
            }
          ) || []
        );
        setTotalRecords(val?.totalRecords);
      }
    });
    setDataLoading(false);
  };

  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteDesignation(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " designation deleted successfully"
            );
            setAddUpdateTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setOpenModel({
        type: "",
        param: {},
      });
    }
  };

  useEffect(() => {
    if (currentClient?.id) {
      getDesignationsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentClient?.id,
    addUpdateTrigger,
    searchText,
    ledgerStatus,
    trigger,
    page,
    pageSize,
    filter,
  ]);

  // --------- Departments Table Columns  ---------
  const depttCols: ColumnsType<DesignationDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Designation's Name",
      dataIndex: "designationName",
      sorter: true,
      defaultSortOrder: "ascend",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department's Name",
      dataIndex: "depttName",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Sub Unit Name",
      dataIndex: "subUnitName",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Organisation Name",
      dataIndex: "orgName",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (_, record) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              {record.status !== LedgerStatus.deleted && (
                <Tooltip
                  title={
                    record?.status === LedgerStatus.active
                      ? "In-Active"
                      : "Active"
                  }
                  placement="top"
                  color="#3D4998"
                >
                  <Checkbox
                    checked={record?.status !== LedgerStatus.deactive}
                    title="Delete"
                    onChange={(e: any) => {
                      // console.log(e.target.checked, record);
                      setOpenModel({
                        type: "isActive",
                        param: {
                          name: record.designationName,
                          id: record.id,
                          itemStatus: record?.status,
                        },
                      });
                      // setSelectChange(record?.isActive ? "In-Active" : "Active");
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <div>
              {record.status === LedgerStatus.active ? (
                <Tooltip title="Edit" placement="top" color="#3D4998">
                  <EditOutlined
                    style={{
                      color: "#444653",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setOpenModel({
                        type: "addDesignation",
                        param: {
                          id: record.id,
                        },
                      });
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </div>
            <div>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setOpenModel({
                      type: "delete",
                      param: {
                        id: record.id,
                        name: record.designationName,
                      },
                    })
                  }
                />
              </Tooltip>
            </div>
          </div>
        </Skeleton>
      ),
    },
  ];

  //----- Table Ascending & Desending Order -----
  const tableSort = (pagination?: any, sort?: any, filter?: any) => {
    setfilter({
      sortCol: filter.column !== undefined ? filter.columnKey : "name",
      sortDir:
        filter.column !== undefined
          ? filter.order === "descend"
            ? "desc"
            : "asc"
          : "desc",
    });
    // console.log("sort1111111111111", sort?.TaxRate?.Rate, sort);
  };

  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <div>
        <Row justify="space-between">
          <Col>
            <Row gutter={15}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setOpenModel({
                      type: "addDesignation",
                      param: {},
                    })
                  }
                >
                  Designation
                </Button>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row gutter={8}>
              <Col>
                <Input
                  allowClear
                  style={{ width: "220px" }}
                  placeholder="Search.."
                  onChange={(e: any) => {
                    setDataLoading(true);
                    if (!e.cancelable) {
                      const searchItemGroup = setTimeout(() => {
                        setSearchText(e.target.value);
                      }, 1000);
                      return () => clearTimeout(searchItemGroup);
                    } else {
                      // console.log("Click cancle............");
                      setSearchText(e.target.value);
                    }
                  }}
                  suffix={dataLoading || searchText ? "" : <SearchOutlined />}
                />
              </Col>
              <Col>
                <Select
                  style={{ width: "110px" }}
                  defaultValue={LedgerStatus.active}
                  onSelect={(e) => {
                    setLedgerStatus(e);
                    // setExportItemsData([]);
                    setPage(1);
                  }}
                  options={LedgerStatusDropDown}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Table
          className={`Tabel-style table-${
            themeData?.componentSize ?? "middle"
          }`}
          columns={depttCols}
          dataSource={
            dataLoading
              ? (Array.from({ length: pageSize }, (_, index) => ({
                  key: `loading-${index}`,
                })) as DesignationDataType[])
              : designationsList
          }
          locale={{
            triggerDesc: "",
            triggerAsc: "",
            cancelSort: "",
          }}
          onChange={tableSort}
          pagination={{
            total: totalRecords,
            current: page,
            pageSize: pageSize,
            showSizeChanger: true,
            onChange: (page: number, pageSize: number) => {
              setPage(page);
              setPageSize(pageSize);
              // setExportType(-1);
            },
            pageSizeOptions: tablePageSizeOptions,
            showTotal: (totalRecords, page) =>
              `${page[0]}-${page[1]} of ${totalRecords} items`,
          }}
        />
      </div>
      {openModel.type === "addDesignation" && (
        <AddDesignationModel
          isModalOpen={true}
          setOpenModel={() => setOpenModel({ type: "", param: {} })}
          id={openModel.param?.id}
          setTrigger={setAddUpdateTrigger}
        />
      )}
      {openModel.type === "delete" && (
        <ConfirmationModal
          open={true}
          onNo={() => setOpenModel({ type: "", param: {} })}
          onYes={() => handleDelete(openModel.param?.id)}
          text={`Are you sure you want to delete this row "${openModel.param?.name}" ?`}
        />
      )}
      {openModel.type === "isActive" && (
        <ConfirmationModal
          open={true}
          onNo={() => setOpenModel({ type: "", param: "" })}
          onYes={async () => {
            try {
              const apiData = await OrganisationsService.isActiveDesignation(
                currentClient?.id!,
                openModel.param?.id,
                openModel.param?.itemStatus === LedgerStatus.deactive
                  ? LedgerStatus.active
                  : LedgerStatus.deactive
              );
              if (apiData.result) {
                openNotification(
                  "success",
                  `Department "${openModel?.param?.name}" ${
                    openModel?.param?.itemStatus === LedgerStatus.deactive
                      ? "Active"
                      : "InActive"
                  } sucessfully.`
                );

                setTrigger((x) => !x);
                setOpenModel({ type: "", param: "" });
              } else {
                openNotification("error", apiData?.message);
                console.log("error", apiData?.message);
                setOpenModel({ type: "", param: "" });
              }
            } catch (err) {
              openNotification("error", err);
              console.log("error", err);
              setOpenModel({ type: "", param: "" });
            }
          }}
          text={`Are you sure you want to ${
            openModel?.param?.itemStatus === LedgerStatus.deactive
              ? "Active"
              : "InActive"
          }  this "${openModel?.param?.name}" Department. ?`}
        />
      )}
    </Card>
  );
};

export default Designations;
